/**
 * Admin Location Assignment Table Component
 *
 * Displays a table of admin location assignments using DataTable.
 * Supports province-grouped rendering when sorting by province.
 */

import * as React from "react";
import { type SortingState } from "@tanstack/react-table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { MapPin, Edit, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import type { AdminLocationAssignment } from "@/services/adminService";
import {
  createLocationAssignmentColumns,
  sortAssignments,
  groupByProvince,
  getPrimaryProvince,
} from "../columns/locationAssignmentColumns";
import { getLevelLabel, getLevelIcon, getLevelColor, formatPrivilege } from "../utils/adminLevelUtils";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";

interface AdminLocationAssignmentTableProps {
  assignments: AdminLocationAssignment[];
  loading: boolean;
  onEdit?: (assignment: AdminLocationAssignment) => void;
  onDelete?: (assignment: AdminLocationAssignment) => void;
}

function formatDate(dateString: string): string {
  try {
    return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: idLocale });
  } catch {
    return "-";
  }
}

// Province-grouped table for when sorting by province
function GroupedTable({
  assignments,
  onEdit,
  onDelete,
}: {
  assignments: AdminLocationAssignment[];
  onEdit?: (a: AdminLocationAssignment) => void;
  onDelete?: (a: AdminLocationAssignment) => void;
}) {
  const groups = groupByProvince(assignments);

  const renderRow = (assignment: AdminLocationAssignment, isChild?: boolean) => (
    <TableRow key={assignment._id}>
      <TableCell className={cn("font-medium", isChild && "pl-6")}>
        <div className="flex flex-col">
          <span className="font-semibold">{assignment.admin?.nama_lengkap ?? "-"}</span>
          <span className="text-xs text-muted-foreground font-mono">
            {assignment.admin?.callsign ?? "-"}
          </span>
        </div>
      </TableCell>
      <TableCell>
        {(() => {
          const Icon = getLevelIcon(assignment.level);
          return (
            <Badge variant="outline" className={cn("flex items-center gap-1 w-fit", getLevelColor(assignment.level))}>
              <Icon className="h-3 w-3" />
              {getLevelLabel(assignment.level)}
            </Badge>
          );
        })()}
      </TableCell>
      <TableCell>
        {assignment.provinces.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {assignment.provinces.slice(0, 2).map((p) => (
              <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
            ))}
            {assignment.provinces.length > 2 && (
              <Badge variant="secondary" className="text-xs">+{assignment.provinces.length - 2}</Badge>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        )}
      </TableCell>
      <TableCell>
        {assignment.cities.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {assignment.cities.slice(0, 2).map((c) => (
              <Badge key={c} variant="secondary" className="text-xs">{c}</Badge>
            ))}
            {assignment.cities.length > 2 && (
              <Badge variant="secondary" className="text-xs">+{assignment.cities.length - 2}</Badge>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        )}
      </TableCell>
      <TableCell>
        {assignment.privileges && assignment.privileges.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {assignment.privileges.map((p) => (
              <Badge key={p} variant="outline" className="text-xs">{formatPrivilege(p)}</Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">-</span>
        )}
      </TableCell>
      <TableCell>
        <Badge
          variant={assignment.isActive ? "default" : "secondary"}
          className={cn(
            assignment.isActive
              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
          )}
        >
          {assignment.isActive ? "Aktif" : "Tidak Aktif"}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatDate(assignment.createdAt)}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          {onEdit && (
            <Button variant="ghost" size="sm" onClick={() => onEdit(assignment)} className="h-8 w-8 p-0">
              <Edit className="h-4 w-4" />
            </Button>
          )}
          {onDelete && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(assignment)} className="h-8 w-8 p-0 text-destructive hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Admin</TableHead>
            <TableHead className="w-[150px]">Level</TableHead>
            <TableHead>Provinsi</TableHead>
            <TableHead>Kota/Kabupaten</TableHead>
            <TableHead>Hak Akses</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
            <TableHead className="w-[180px]">Dibuat</TableHead>
            <TableHead className="w-[120px] text-right">Aksi</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.flatMap((group) => [
            <TableRow key={`province-header:${group.province}`}>
              <TableCell colSpan={8} className="bg-muted/40">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">
                    {group.province === "NASIONAL" ? "Nasional" : group.province}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {group.parents.length + group.children.length} penugasan
                  </div>
                </div>
              </TableCell>
            </TableRow>,
            ...group.parents.map((a) => renderRow(a)),
            ...group.children.map((a) => renderRow(a, true)),
          ])}
        </TableBody>
      </Table>
    </div>
  );
}

// Sortable header for the grouped view
function SortHeader({
  label,
  field,
  sortBy,
  sortOrder,
  onSort,
}: {
  label: string;
  field: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (field: string) => void;
}) {
  const active = sortBy === field;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 px-2 font-semibold"
      onClick={() => onSort(field)}
    >
      {label}
      {active ? (
        sortOrder === "asc" ? <ArrowUp className="ml-2 h-3 w-3" /> : <ArrowDown className="ml-2 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-2 h-3 w-3 text-muted-foreground" />
      )}
    </Button>
  );
}

export function AdminLocationAssignmentTable({
  assignments,
  loading,
  onEdit,
  onDelete,
}: AdminLocationAssignmentTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "province", desc: false },
  ]);

  const sortBy = sorting[0]?.id ?? "province";
  const sortOrder = (sorting[0]?.desc ? "desc" : "asc") as "asc" | "desc";
  const isGrouped = sortBy === "province" || sortBy === "provinces";

  const columns = React.useMemo(
    () => createLocationAssignmentColumns({ onEdit, onDelete }),
    [onEdit, onDelete]
  );

  const sortedData = React.useMemo(
    () => sortAssignments(assignments, sortBy, sortOrder),
    [assignments, sortBy, sortOrder]
  );

  const handleSort = React.useCallback((field: string) => {
    setSorting((prev) => {
      const current = prev[0];
      if (current?.id === field) {
        return [{ id: field, desc: !current.desc }];
      }
      return [{ id: field, desc: field === "province" ? false : true }];
    });
  }, []);

  const cardHeader = (
    <CardHeader>
      <CardTitle>Penugasan Lokasi Admin</CardTitle>
      <CardDescription>
        Daftar penugasan lokasi untuk admin ({assignments.length} total)
      </CardDescription>
    </CardHeader>
  );

  if (loading) {
    return (
      <Card>
        {cardHeader}
        <CardContent>
          <DataTable
            columns={columns}
            data={[]}
            loading
            emptyMessage="Belum ada penugasan lokasi"
            emptyIcon={<MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />}
          />
        </CardContent>
      </Card>
    );
  }

  if (assignments.length === 0) {
    return (
      <Card>
        {cardHeader}
        <CardContent>
          <DataTable
            columns={columns}
            data={[]}
            emptyMessage="Belum ada penugasan lokasi"
            emptyIcon={<MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />}
          />
        </CardContent>
      </Card>
    );
  }

  // Province-grouped view
  if (isGrouped) {
    return (
      <Card>
        {cardHeader}
        <CardContent>
          <GroupedTable assignments={sortedData} onEdit={onEdit} onDelete={onDelete} />
        </CardContent>
      </Card>
    );
  }

  // Flat DataTable view
  return (
    <Card>
      {cardHeader}
      <CardContent>
        <DataTable
          columns={columns}
          data={sortedData}
          manualSorting
          sorting={sorting}
          onSortingChange={setSorting}
          emptyMessage="Belum ada penugasan lokasi"
          emptyIcon={<MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />}
        />
      </CardContent>
    </Card>
  );
}
